import type { TFunction } from 'i18next';

import { ApiError } from './client';

export interface ErrorText {
  message: string;
  remedy?: string;

  /// The server's own account of what happened, in English, for where the translated message
  /// is a headline and the particulars - what a worker said, which stream it was reading - are
  /// worth having beside it. Not filled in here: most failures say everything in the message,
  /// and only a caller that wants the particulars as well takes them from the error.
  detail?: string;
}

/// Turns a failed request into text a person can read.
///
/// Every failure the server sends carries a stable code, an English message and, where there
/// is something to do about it, a remedy. The code is the translation key - for a plugin it is
/// namespaced, `plugin.tv.emiro.filesystem.location.not_found`. The English text is the
/// fallback, which beats showing a raw key when a translation is missing.
export function apiErrorText(error: unknown, t: TFunction, context?: string): ErrorText {
  if (!(error instanceof ApiError)) {
    return { message: error instanceof Error ? error.message : String(error) };
  }

  if (!error.code) {
    return { message: error.message, remedy: error.remedy };
  }

  // The problem document's own fields are the interpolation values: the path that was
  // rejected, the permission that was missing, and so on.
  const values = { ...error.data, context };

  return {
    message: t(error.code, { ...values, defaultValue: error.message }),
    remedy: error.remedy
      ? t(`${error.code}.remedy`, { ...values, defaultValue: error.remedy })
      : undefined,
  };
}
